import React, { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { User, CreateUserForm, Role } from '../../types';
import PhoneInput from './PhoneInput';
import { Input } from '../ui/input';

const userSchema = yup.object({
  email: yup.string().email('Invalid email format').required('Email is required').max(256, 'Email must be less than 256 characters'),
  password: yup.string().when('$isEdit', {
    is: false,
    then: (schema) => schema.required('Password is required').min(6, 'Password must be at least 6 characters'),
    otherwise: (schema) => schema.optional()
  }),
  firstName: yup.string().required('First name is required').max(50, 'First name must be less than 50 characters'),
  lastName: yup.string().required('Last name is required').max(50, 'Last name must be less than 50 characters'),
  phone: yup.string().max(20, 'Phone must be less than 20 characters').optional(),
  city: yup.string().max(100, 'City must be less than 100 characters').optional(),
  state: yup.string().max(50, 'State must be less than 50 characters').optional(),
  zipCode: yup.string().max(20, 'Zip code must be less than 20 characters').optional(),
  roleId: yup.number().required('Role is required').min(1, 'Please select a role'),
  isActive: yup.boolean().optional()
});

interface UserFormProps {
  user?: User;
  roles: Role[];
  onSubmit: (data: CreateUserForm | Omit<CreateUserForm, 'password'>) => void;
  onCancel?: () => void;
  isLoading?: boolean;
  error?: string;
  canDeactivate?: boolean;
}

const UserForm: React.FC<UserFormProps> = ({ user, roles, onSubmit, onCancel, isLoading = false, error, canDeactivate = true }) => {
  const isEdit = !!user;
  
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset
  } = useForm<CreateUserForm>({
    resolver: yupResolver(userSchema),
    context: { isEdit },
    defaultValues: {
      email: user?.email || '',
      password: '',
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      phone: user?.phone || '',
      city: user?.city || '',
      state: user?.state || '',
      zipCode: user?.zipCode || '',
      roleId: user?.roleId || 0,
      sendWelcomeEmail: true,
      forcePasswordChange: true,
      isActive: user?.isActive ?? true
    }
  });

  useEffect(() => {
    if (user) {
      reset({
        email: user.email,
        password: '',
        firstName: user.firstName,
        lastName: user.lastName,
        phone: user.phone || '',
        city: user.city || '',
        state: user.state || '',
        zipCode: user.zipCode || '',
        roleId: user.roleId,
        isActive: user.isActive
      });
    }
  }, [user, reset]);

  const handleFormSubmit = (data: CreateUserForm) => {
    if (isEdit) {
      // For edit, exclude password and email options if it's empty
      const { password, sendWelcomeEmail, forcePasswordChange, ...updateData } = data;
      onSubmit(updateData);
    } else {
      // For create, include all fields
      onSubmit(data);
    }
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-800 text-sm">{error}</div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Email */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Email <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            id="email"
            {...register('email')}
            className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-accent-color focus:border-accent-color bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 !bg-white dark:!bg-gray-700 !text-gray-900 dark:!text-gray-100"
            placeholder="Enter email address"
          />
          {errors.email && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.email.message}</p>}
        </div>

        {/* First Name */}
        <div>
          <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            First Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="firstName"
            {...register('firstName')}
            className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-accent-color focus:border-accent-color bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 !bg-white dark:!bg-gray-700 !text-gray-900 dark:!text-gray-100"
            placeholder="Enter first name"
          />
          {errors.firstName && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.firstName.message}</p>}
        </div>

        {/* Last Name */}
        <div>
          <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Last Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="lastName"
            {...register('lastName')}
            className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-accent-color focus:border-accent-color bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 !bg-white dark:!bg-gray-700 !text-gray-900 dark:!text-gray-100"
            placeholder="Enter last name"
          />
          {errors.lastName && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.lastName.message}</p>}
        </div>

        {/* Phone */}
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Phone
          </label>
          <Controller
            name="phone"
            control={control}
            render={({ field }) => (
              <PhoneInput
                value={field.value || ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                placeholder="Enter phone number"
                className=""
              />
            )}
          />
          {errors.phone && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.phone.message}</p>}
        </div>

        {/* City */}
        <div>
          <label htmlFor="city" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            City
          </label>
          <input
            type="text"
            id="city"
            {...register('city')}
            className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-accent-color focus:border-accent-color bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 !bg-white dark:!bg-gray-700 !text-gray-900 dark:!text-gray-100"
            placeholder="Enter city"
          />
          {errors.city && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.city.message}</p>}
        </div>

        {/* State */}
        <div>
          <label htmlFor="state" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            State
          </label>
          <input
            type="text"
            id="state"
            {...register('state')}
            className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-accent-color focus:border-accent-color bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 !bg-white dark:!bg-gray-700 !text-gray-900 dark:!text-gray-100"
            placeholder="Enter state"
          />
          {errors.state && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.state.message}</p>}
        </div>

        {/* Zip Code */}
        <div>
          <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Zip Code
          </label>
          <input
            type="text"
            id="zipCode"
            {...register('zipCode')}
            className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-accent-color focus:border-accent-color bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 !bg-white dark:!bg-gray-700 !text-gray-900 dark:!text-gray-100"
            placeholder="Enter zip code"
          />
          {errors.zipCode && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.zipCode.message}</p>}
        </div>

        {/* Password */}
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Password {!isEdit && <span className="text-red-500">*</span>}
          </label>
          <input
            type="password"
            id="password"
            {...register('password')}
            className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-accent-color focus:border-accent-color bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            placeholder={isEdit ? "Leave blank to keep current password" : "Enter password"}
          />
          {errors.password && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.password.message}</p>}
          {isEdit && (
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Leave blank to keep current password</p>
          )}
        </div>

        {/* Role */}
        <div>
          <label htmlFor="roleId" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Role *
          </label>
          <Controller
            name="roleId"
            control={control}
            render={({ field }) => (
              <select
                {...field}
                className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-accent-color focus:border-accent-color bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 !bg-white dark:!bg-gray-700 !text-gray-900 dark:!text-gray-100"
              >
                <option value={0}>Select a role</option>
                {roles.filter((role) => role.name !== 'SuperAdmin').map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </select>
            )}
          />
          {errors.roleId && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.roleId.message}</p>}
        </div>
      </div>

      {isEdit && (
        <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
          <Controller
            name="isActive"
            control={control}
            render={({ field: { onChange, value, ref } }) => (
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    ref={ref}
                    type="checkbox"
                    id="isActive"
                    checked={!!value}
                    onChange={(e) => onChange(e.target.checked)}
                    disabled={!canDeactivate}
                    className="h-4 w-4 rounded border-gray-300 text-accent-color focus:ring-accent-color dark:border-gray-600 dark:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label="User is active"
                  />
                </div>
                <div className="ml-3">
                  <label htmlFor="isActive" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Active
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {canDeactivate
                      ? 'Inactive users cannot log in. They do not count toward your plan user limit.'
                      : 'You cannot deactivate your own account.'}
                  </p>
                </div>
              </div>
            )}
          />
        </div>
      )}

      {/* Email and Password Options - Only show when creating new user */}
      {!isEdit && (
        <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Account Setup Options</h3>
          <div className="space-y-3">
            {/* Send Welcome Email */}
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  type="checkbox"
                  id="sendWelcomeEmail"
                  {...register('sendWelcomeEmail')}
                  className="h-4 w-4 rounded border-gray-300 text-accent-color focus:ring-accent-color dark:border-gray-600 dark:bg-gray-700"
                />
              </div>
              <div className="ml-3">
                <label htmlFor="sendWelcomeEmail" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Send Welcome Email
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Send login credentials and instructions to the new user's email address
                </p>
              </div>
            </div>

            {/* Force Password Change */}
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  type="checkbox"
                  id="forcePasswordChange"
                  {...register('forcePasswordChange')}
                  className="h-4 w-4 rounded border-gray-300 text-accent-color focus:ring-accent-color dark:border-gray-600 dark:bg-gray-700"
                />
              </div>
              <div className="ml-3">
                <label htmlFor="forcePasswordChange" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Require Password Change on First Login
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  User will be required to change their password when they first log in
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Submit Button */}
      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel || (() => window.history.back())}
          className="btn-secondary"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Saving...' : (isEdit ? 'Update User' : 'Create User')}
        </button>
      </div>
    </form>
  );
};

export default UserForm;
